import type { TrackerConfig } from './contracts';

// Format 1 spells the ids out with dots; format 2 packs them. Both are read, format 2 is written.
export const CONTEXT_VERSION = '1';
export const COMPACT_CONTEXT_VERSION = '2';
export const WORKSPACE_PREFIX_LENGTH = 8;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// 2, mode, workspace prefix (8), unix seconds in base36 (7), two uuids in base64url (22 each): 61 characters.
const COMPACT_LINK_PATTERN = /^2([ap])([A-Za-z0-9_-]{8})([0-9a-z]{7})([A-Za-z0-9_-]{22})([A-Za-z0-9_-]{22})$/;
const COMPACT_SECONDS_WIDTH = 7;
// The characters of the ids the SDK generates. A link must not store an id that the ingestion service trims or rejects.
const LINKED_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

const isLinkedId = (value: string): boolean => LINKED_ID_PATTERN.test(value);

/** Current browser context, shared by navigation links and server event handoff. */
export const encodeTrackingContext = (
  config: Pick<TrackerConfig, 'workspaceId' | 'privacyMode'>,
  ids: { visitorId: string; sessionId: string },
  now: number
): string | null => {
  const workspacePrefix = config.workspaceId.slice(0, WORKSPACE_PREFIX_LENGTH);
  if (!workspacePrefix || workspacePrefix.includes('.')) return null;
  if (!isLinkedId(ids.visitorId) || !isLinkedId(ids.sessionId)) return null;
  const mode = config.privacyMode === 'anonymous' ? 'a' : 'p';
  const seconds = Math.floor(now / 1_000);
  if (
    workspacePrefix.length === WORKSPACE_PREFIX_LENGTH && isLinkedId(workspacePrefix)
    && UUID_PATTERN.test(ids.visitorId) && UUID_PATTERN.test(ids.sessionId)
  ) {
    return `${COMPACT_CONTEXT_VERSION}${mode}${workspacePrefix}${seconds.toString(36).padStart(COMPACT_SECONDS_WIDTH, '0')}${encodeUuid(ids.visitorId)}${encodeUuid(ids.sessionId)}`;
  }
  // Ids that are not uuids, such as the fallback ids of a browser without crypto.randomUUID, keep the spelled-out format.
  return [CONTEXT_VERSION, mode, workspacePrefix, ids.visitorId, ids.sessionId, String(seconds)].join('.');
};

const encodeUuid = (uuid: string): string => {
  const hex = uuid.replace(/-/g, '').toLowerCase();
  let binary = '';
  for (let index = 0; index < hex.length; index += 2) binary += String.fromCharCode(parseInt(hex.slice(index, index + 2), 16));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const decodeUuid = (value: string): string | null => {
  let binary: string;
  try {
    binary = atob(`${value.replace(/-/g, '+').replace(/_/g, '/')}==`);
  } catch {
    return null;
  }
  if (binary.length !== 16) return null;
  const hex = Array.from(binary, (character) => character.charCodeAt(0).toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export type ParsedTrackingContext = {
  version: string;
  mode: string;
  workspacePrefix: string;
  visitorId: string;
  sessionId: string;
  seconds: number;
};

/** The fields of a linker value in either format, or null when it has neither shape. */
export const parseTrackingContext = (value: string): ParsedTrackingContext | null => {
  const compact = COMPACT_LINK_PATTERN.exec(value);
  if (compact) {
    const [, mode = '', workspacePrefix = '', seconds = '', visitor = '', session = ''] = compact;
    const visitorId = decodeUuid(visitor);
    const sessionId = decodeUuid(session);
    if (!visitorId || !sessionId) return null;
    return { version: COMPACT_CONTEXT_VERSION, mode, workspacePrefix, visitorId, sessionId, seconds: parseInt(seconds, 36) };
  }
  const parts = value.split('.');
  if (parts.length !== 6) return null;
  const [version = '', mode = '', workspacePrefix = '', visitorId = '', sessionId = '', seconds = ''] = parts;
  if (!isLinkedId(visitorId) || !isLinkedId(sessionId) || !/^\d{1,12}$/.test(seconds)) return null;
  return { version, mode, workspacePrefix, visitorId, sessionId, seconds: Number(seconds) };
};

